import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { Transition } from '@headlessui/react';
import Logo from './Logo';

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: "#features", label: "Features" },
    { href: "#workflow", label: "How It Works" },
    { href: "#testimonials", label: "Testimonials" },
    { href: "#benefits", label: "Benefits" }
  ];

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    setIsOpen(false);
    const href = e.currentTarget.getAttribute('href');
    if (href?.startsWith('#')) {
      e.preventDefault();
      const element = document.querySelector(href);
      if (element) {
        const offset = 80;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }
  };

  return (
    <nav className="fixed w-full z-50 px-4 py-4">
      <div className="container mx-auto">
        <div className={`transition-all duration-300 rounded-2xl ${
          scrolled ? 'bg-white shadow-[0_2px_0_0_rgba(0,0,0,0.05)]' : 'bg-white/80'
        } px-6 py-4`}>
          <div className="flex justify-between items-center">
            <a href="#" className="flex items-center space-x-2">
              <Logo />
            </a>

            <div className="hidden md:flex items-center space-x-1">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={handleNavClick}
                  className="px-4 py-2 text-gray-700 hover:text-[#2196F3] transition-colors rounded-lg hover:bg-gray-50 font-medium text-sm"
                >
                  {link.label}
                </a>
              ))}
              <a
                href="https://portal.smartistat.com"
                className="ml-4 px-6 py-2 bg-gradient-to-r from-[#2196F3] to-[#4CAF50] text-white rounded-lg font-medium text-sm hover:opacity-90 transition-opacity"
              >
                E-Portal
              </a>
            </div>

            <button 
              onClick={() => setIsOpen(!isOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-[#2196F3] focus:ring-opacity-50"
              aria-label="Toggle menu"
              aria-expanded={isOpen}
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          <Transition
            show={isOpen}
            enter="transition-all duration-300 ease-out"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="transition-all duration-200 ease-in"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
            className="md:hidden absolute left-4 right-4 top-[calc(100%+1rem)] z-50"
          >
            <div className="bg-white rounded-xl shadow-[0_2px_0_0_rgba(0,0,0,0.05)] ring-1 ring-black ring-opacity-5 overflow-hidden p-4">
              <div className="flex flex-col space-y-2">
                {navLinks.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={handleNavClick}
                    className="px-4 py-3 text-gray-700 hover:text-[#2196F3] hover:bg-gray-50 rounded-lg transition-colors text-sm font-medium active:bg-gray-100"
                  >
                    {link.label}
                  </a>
                ))}
                <div className="pt-2">
                  <a
                    href="https://portal.smartistat.com"
                    className="block px-4 py-3 text-center text-white bg-gradient-to-r from-[#2196F3] to-[#4CAF50] rounded-lg text-sm font-medium hover:opacity-90 transition-opacity active:opacity-100"
                    onClick={() => setIsOpen(false)}
                  >
                    E-Portal
                  </a>
                </div>
              </div>
            </div>
          </Transition>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;